/* Reusable date/time constants and utility functions. */
import { CURRENT_MONTH, CURRENT_YEAR, DAYS_OF_WEEK, DAYS_OF_WEEK_SHORT, MILLISECONDS_PER_DAY, MONTHS_LONG, MONTHS_SHORT, } from '@variables';
/* Converts between age in years/months and birthday years/months. */
var convertAgeBirthdayYearMonths = function (month, year) {
    if ((!month && month !== 0 && !year && year !== 0) || month < 0 || year < 0) {
        return [null, null];
    }
    var deltaAmount = (CURRENT_YEAR - year) * 12 + CURRENT_MONTH - month;
    var ageMonth = deltaAmount % 12;
    var ageYear = (deltaAmount - ageMonth) / 12;
    return [ageMonth, ageYear];
};
var getMonthsDiff = function (startDate, endDate) {
    var months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
    months -= startDate.getMonth();
    months += endDate.getMonth();
    return months <= 0 ? 0 : months;
};
var getDaysAge = function (dateStr) {
    if (!dateStr) {
        return;
    }
    var startDate = new Date(dateStr);
    var now = new Date();
    return Math.floor((now.getTime() - startDate.getTime()) / MILLISECONDS_PER_DAY);
};
/**
 * Get the Sunday before a given Date.
 * @param {Date} date Starting date to find previous Sunday from.
 * @return {Date} The Sunday before the date provided.
 */
var getPreviousSunday = function (date) {
    var previousSunday = new Date(date);
    previousSunday.setDate(date.getDate() - date.getDay());
    return previousSunday;
};
/**
 * Get the Monday before a given Date.
 * @param {Date} date Starting date to find previous Monday from.
 * @return {Date} The Monday before the date provided.
 */
var getPreviousMonday = function (date) {
    var previousMonday = new Date(date);
    previousMonday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    return previousMonday;
};
/**
 * Converts birthday year, month and day inputs into a string age to display.
 * @param {number} birthdayYear Year of pet birthday.
 * @param {birthdayMonth} birthdayMonth Birthday month. Defaults to January.
 * @param {birthdayDay} birthdayDay Birthday day. Defaults to 1st day of month.
 * @return {string} Human-readable display of age in years and months.
 */
var convertBirthdayToAgeText = function (birthdayYear, birthdayMonth, birthdayDay, useAndConjunction) {
    if (birthdayMonth === void 0) { birthdayMonth = 1; }
    if (birthdayDay === void 0) { birthdayDay = 1; }
    if (useAndConjunction === void 0) { useAndConjunction = true; }
    if (!birthdayYear) {
        return;
    }
    if (birthdayDay === null) {
        birthdayDay = 1;
    }
    var birthdayDate = new Date(birthdayYear, birthdayMonth - 1, birthdayDay);
    var now = new Date();
    var monthsDiff = getMonthsDiff(birthdayDate, now);
    if (monthsDiff === 0) {
        return;
    }
    if (monthsDiff < 12) {
        return "".concat(monthsDiff, " month").concat(monthsDiff > 1 ? 's' : '');
    }
    if (monthsDiff < 24) {
        var months = monthsDiff - 12;
        return "1 year".concat(useAndConjunction ? ' and' : ', ', " ").concat(months, " month").concat(months > 1 ? 's' : '');
    }
    return "".concat(Math.floor(monthsDiff / 12), " years");
};
/* Converts a date string to a human readable day of the week. */
var convertDateStrDayOfWeek = function (dateStr, useLongDay) {
    if (useLongDay === void 0) { useLongDay = true; }
    return (useLongDay ? DAYS_OF_WEEK : DAYS_OF_WEEK_SHORT)[new Date(dateStr).getUTCDay()];
};
/* Calculates the difference between two dates in weeks. */
var getWeeksDiff = function (firstDate, secondDate) {
    var weeks = (secondDate.getTime() - firstDate.getTime()) / (MILLISECONDS_PER_DAY * 7);
    return Math.round(weeks);
};
/* Converts a date string to a human readable formatted date for display. */
var convertDateStrDisplayDate = function (dateStr, includeYear) {
    if (includeYear === void 0) { includeYear = false; }
    if (!dateStr) {
        return;
    }
    var newDate = new Date(dateStr);
    var dayDate = "".concat(DAYS_OF_WEEK[newDate.getUTCDay()].slice(0, 3)) +
        ", ".concat(MONTHS_SHORT[newDate.getUTCMonth()], " ").concat(newDate.getUTCDate());
    return includeYear ? "".concat(dayDate, ", ").concat(newDate.getFullYear()) : dayDate;
};
/* Converts a date string to a human readable formatted date for display. */
var convertDateStrMonthDay = function (dateStr, includeYear, useMonthLong) {
    if (includeYear === void 0) { includeYear = false; }
    if (useMonthLong === void 0) { useMonthLong = false; }
    if (!dateStr) {
        return;
    }
    var newDate = new Date(dateStr);
    var monthDayStr = "".concat(useMonthLong ? MONTHS_LONG[newDate.getUTCMonth()] : MONTHS_SHORT[newDate.getUTCMonth()], " ").concat(newDate.getUTCDate());
    return includeYear ? "".concat(monthDayStr, ", ").concat(newDate.getFullYear()) : monthDayStr;
};
/* Convert date to string and format for display: Long Month Year, ex. January 2023 */
var convertDateToMonthYearStr = function (dateStr) {
    if (!dateStr) {
        return;
    }
    var newDate = new Date(dateStr);
    return "".concat(MONTHS_LONG[newDate.getMonth()], " ").concat(newDate.getFullYear());
};
/* Converts a YYYY-MM-DD format date string into a local date object. */
var convertDateStrToDate = function (dateStr) {
    if (!dateStr) {
        return;
    }
    // Append time to date string to prevent assuming date is UTC start of day.
    // In other words, the input and output dates will match.
    return new Date("".concat(dateStr, " 00:00:00"));
};
/* Formatting function to convert date to string ISO date. */
var convertDateToIsoDateStr = function (date) {
    if (!date) {
        return;
    }
    var day = date.getDate();
    var month = date.getMonth() + 1;
    var zeroFilledMonth = (month > 9 ? '' : '0') + month;
    var zeroFilledDay = (day > 9 ? '' : '0') + day;
    return "".concat(date.getFullYear(), "-").concat(zeroFilledMonth, "-").concat(zeroFilledDay);
};
/**
 * Converts a date string from format YYYY-MM-DDTHH:MM:SS.XXXXX to YYYY-MM-DD HH:MM:SS
 * @param {string} dateStr Date string
 * @param {boolean} includeTime Boolean indicates returning string to include HH:MM:SS
 * @return {string} Date string with format YYYY-MM-DD or YYYY-MM-DD HH:MM:SS
 */
var formatIsoDateStr = function (dateStr, includeTime) {
    if (includeTime === void 0) { includeTime = true; }
    if (!dateStr) {
        return;
    }
    var regexResult = dateStr.match(/^(\d{4})-(\d\d)-(\d\d)T(\d\d):(\d\d):(\d\d)/);
    if (regexResult) {
        var _a = regexResult[0].split('T'), date = _a[0], time = _a[1];
        return includeTime ? "".concat(date, " ").concat(time.split('.')[0]) : date;
    }
    return dateStr;
};
/**
 * Get the zero-padded ordinal Date.
 *  @example
 *    getOrdinalDate(new Date('2023-1-1T00:00') // '23001'
 *    getOrdinalDate(new Date('2023-2-6T00:00') // '23037'
 *    getOrdinalDate(new Date('2023-5-1T00:00') // '23121'
 * @param {Date} date Localized date to convert to the ordinal date.
 * @return {string} The ordinal date.
 */
var getOrdinalDate = function (date) {
    var year = date.getFullYear().toString().substring(2);
    var dayOfYear = (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
        Date.UTC(date.getFullYear(), 0, 0)) /
        MILLISECONDS_PER_DAY;
    var zeroPadding = '0'.repeat(3 - dayOfYear.toString().length);
    return "".concat(year).concat(zeroPadding).concat(dayOfYear);
};
/**
 * Create and return a new Date object with the given number of days added to the input date.
 * @param {Date} date Starting Date object.
 * @param {number} days Number of days to add to the input date.
 * @return {Date} A new Date object with the added days.
 */
var getFutureDate = function (date, days) {
    var newDate = new Date(date.valueOf());
    newDate.setDate(newDate.getDate() + days);
    return newDate;
};
// Determine a probable puppy status based on birthday and age inputs
// If inputs are valid and the age is less than 1 year and 6 months, return true
// Otherwise return false
var determineIsLikelyPuppy = function (birthdayYear, birthdayMonth, ageYear, ageMonth) {
    if (ageYear === void 0) { ageYear = 0; }
    if (ageMonth === void 0) { ageMonth = 0; }
    if (birthdayYear && birthdayMonth && (ageYear < 1 || (ageYear === 1 && ageMonth <= 6))) {
        return true;
    }
    return false;
};
var estimatedTimeElapsed = function (t) {
    var now = Date.now();
    var diff = (now - t) / 1000;
    if (diff > 86400) {
        var days = Math.round(diff / 86400);
        return "".concat(days, " days ago");
    }
    else if (diff > 3600) {
        var hours = Math.round(diff / 3600);
        return "".concat(hours, " hours ago");
    }
    else if (diff > 60) {
        var minutes = Math.round(diff / 60);
        return "".concat(minutes, " minutes ago");
    }
    else {
        var seconds = Math.round(diff);
        return "".concat(seconds, " seconds ago");
    }
};
export { getFutureDate, convertAgeBirthdayYearMonths, convertBirthdayToAgeText, convertDateStrToDate, convertDateStrDayOfWeek, convertDateStrDisplayDate, convertDateStrMonthDay, convertDateToIsoDateStr, convertDateToMonthYearStr, determineIsLikelyPuppy, estimatedTimeElapsed, formatIsoDateStr, getDaysAge, getOrdinalDate, getPreviousMonday, getPreviousSunday, getWeeksDiff, };
